import React, { useState, useCallback, useRef } from 'react'

import {
  Button,
  TextInput,
  Icon,
  Upload,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  func, arrayOf, shape, string, bool, number } from 'prop-types'

import useUseCaseLogger from 'Pods/LazyLoaded/UseCaseAreaSidebar/hooks/useUseCaseLogger'
import useUseCaseTranslate from 'Pods/LazyLoaded/UseCaseAreaSidebar/hooks/useUseCaseTranslate'
import {
  buildEmbedFromUrl,
  parseWebUrl,
  MAX_EMBED_URL_LENGTH,
} from 'Pods/LazyLoaded/UseCaseAreaSidebar/Utils/Video'
import generateUuid from 'Pods/Shared/Utils/generateUuid'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import MediaDropZone from './Components/MediaDropZone/MediaDropZone'
import UploadMediaItem from './Components/UploadMediaItem/UploadMediaItem'
import { getValidFiles, ACCEPTED_FILE_TYPES, MAX_FILE_SIZE, bytesToGB } from '../../../../Utils/fileValidation'
import { useCaseShape } from '../../../../Utils/propTypes'

const MAX_FILES = 5

function FileUploadSection({
  isProcessing,
  onFilesAdded: handleFilesAdded,
  onEmbedAdded: handleEmbedAdded,
  onRemoveMedia: handleRemoveMedia,
  onReorderMedia: handleReorderMedia = null,
  onShowNotification: handleShowNotification,
  pendingMedia,
  progress,
  useCases,
  // Optional props for edit mode
  existingMedia = EMPTY_ARRAY,
  onRemoveExisting = null,
  enableDragAndDrop = true,
}) {
  const { t, tv } = useUseCaseTranslate()
  const [embedUrl, setEmbedUrl] = useState('')
  const fileInputRef = useRef(null)
  const { logError } = useUseCaseLogger()

  const handleMoveMedia = useCallback((dragIndex, hoverIndex) => {
    if (dragIndex === hoverIndex) return

    const newMedia = [...(existingMedia.length > 0 ? existingMedia : pendingMedia)]
    const draggedItem = newMedia[dragIndex]

    newMedia.splice(dragIndex, 1)
    newMedia.splice(hoverIndex, 0, draggedItem)

    const mediaWithUpdatedPositions = newMedia.map((media, index) => ({
      ...media,
      position: index + 1,
    }))

    if (handleReorderMedia) {
      handleReorderMedia(mediaWithUpdatedPositions)
    }
  }, [existingMedia, pendingMedia, handleReorderMedia])

  const handleFileChange = (event) => {
    const files = event.target.files
    const input = fileInputRef.current

    if (files && files.length > 0) {
      const totalExisting = existingMedia.length + pendingMedia.length
      const { validFiles, errors } = getValidFiles(files, totalExisting, MAX_FILES, t)

      if (errors.length > 0) {
        // Checked by size, not by message text, because the messages are translated.
        const oversizedFiles = Array.from(files).filter(file => file.size > MAX_FILE_SIZE)

        if (oversizedFiles.length > 0) {
          oversizedFiles.forEach((file) => {
            const error = new Error(`File size limit exceeded: ${file.name}`)

            logError('file_upload_size_exceeded', error, {
              fileName: file.name,
              fileSizeInGB: bytesToGB(file.size),
              fileSizeInBytes: file.size,
              maxFileSizeGB: bytesToGB(MAX_FILE_SIZE, 1),
            })
          })
        }

        handleShowNotification(errors.join('. '), 'error')
      }

      if (validFiles.length > 0) {
        handleFilesAdded(validFiles)
      }

      if (input) {
        input.value = ''
      }
    }
  }

  const handleBrowseClick = () => {
    if (fileInputRef.current && !isProcessing) {
      fileInputRef.current.click()
    }
  }

  const isDuplicateEmbed = (url, externalId) => {
    const duplicateInPending = pendingMedia.some(media =>
      media.url === url || (media.externalId && media.externalId === externalId)
    )

    if (duplicateInPending) return true

    const duplicateInExisting = useCases.some(useCase =>
      useCase.useCaseEmbeds.some(embed =>
        embed.url === url || (embed.externalId && embed.externalId === externalId)
      )
    )

    return duplicateInExisting
  }

  const handleEmbedSubmit = () => {
    const totalExisting = existingMedia.length + pendingMedia.length
    if (totalExisting >= MAX_FILES) {
      handleShowNotification(t('errors.maxFiles', { count: MAX_FILES }), 'error')
      return
    }

    const webUrl = parseWebUrl(embedUrl)

    if (!webUrl) {
      handleShowNotification(t('errors.invalidLink'), 'error')
      return
    }

    const embed = buildEmbedFromUrl(webUrl)

    if (embed.externalId.length > MAX_EMBED_URL_LENGTH) {
      handleShowNotification(t('errors.linkTooLong', { count: MAX_EMBED_URL_LENGTH }), 'error')
      return
    }

    if (isDuplicateEmbed(embed.url, embed.externalId)) {
      handleShowNotification(tv('errors.duplicateMedia'), 'error')
      return
    }

    handleEmbedAdded({ ...embed, id: generateUuid() })

    setEmbedUrl('')
  }

  return (
    <div className="use-cases-area__file-upload">
      {isProcessing ? (
        <div className="use-cases-area__processing-indicator">
          <p className="cgt-legacy-text-lead">
            {t('form.processing', { percentage: progress?.percentage || 0 })}
          </p>
          <p className="cgt-legacy-text-default">
            {t('form.processedCount', { completed: progress?.completed || 0, total: progress?.total || 0 })}
          </p>
        </div>
      ) : (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            multiple
            onChange={handleFileChange}
            disabled={isProcessing}
            style={{ display: 'none' }}
            aria-label={t('form.uploadFiles')}
          />
          <Button
            type="secondary"
            onClick={handleBrowseClick}
            disabled={isProcessing}
            customClass="use-cases-area__file-upload-button"
            expand
          >
            <span className="use-cases-area__file-upload-button-content">
              <Icon svgComponent={Upload} size="small" color="dark" />
              {t('form.upload', { count: MAX_FILES })}
            </span>
          </Button>
        </>
      )}

      {(existingMedia.length > 0 || pendingMedia.length > 0) && (
        <div className="use-cases-area__media-preview">
          {/* All media items (existing + pending) */}
          {existingMedia.map((media, index) => {
            const mediaItem = (
              <UploadMediaItem
                media={{
                  ...media,
                  preview: media.preview || media.thumbUrl || media.mediumUrl || media.url,
                  type: media.type || (media.source === 'upload' ? 'image' : 'video'),
                }}
                index={index}
                onRemove={() => onRemoveExisting?.(media.id)}
              />
            )

            return enableDragAndDrop ? (
              <MediaDropZone key={media.id} index={index} onMove={handleMoveMedia}>
                {mediaItem}
              </MediaDropZone>
            ) : (
              <div key={media.id} className="use-cases-area__media-item-wrapper">
                {mediaItem}
              </div>
            )
          })}

          {/* Pending media (should be empty in edit mode) */}
          {pendingMedia.map((media, index) => (
            <MediaDropZone
              key={media.id}
              index={index + existingMedia.length}
              onMove={handleMoveMedia}
            >
              <UploadMediaItem
                media={media}
                index={index + existingMedia.length}
                onRemove={() => handleRemoveMedia(media.id)}
              />
            </MediaDropZone>
          ))}
        </div>
      )}

      <div className="use-cases-area__embed-section">
        <TextInput
          value={embedUrl}
          onChange={(e) => setEmbedUrl(e.target.value)}
          placeholder={t('form.linkPlaceholder')}
          disabled={isProcessing}
          size="small"
          id="embed-url-input"
          isPilot
          trailButton={
            <Button
              noWrap
              onClick={handleEmbedSubmit}
              size="xsmall"
              type="primary"
              disabled={!embedUrl.trim() || isProcessing}
            >
              {t('form.add')}
            </Button>
          }
        />
      </div>
    </div>
  )
}

FileUploadSection.propTypes = {
  isProcessing: bool.isRequired,
  onFilesAdded: func.isRequired,
  onEmbedAdded: func.isRequired,
  onRemoveMedia: func.isRequired,
  onReorderMedia: func,
  onShowNotification: func.isRequired,
  pendingMedia: arrayOf(shape({
    id: string.isRequired,
    type: string.isRequired,
    source: string.isRequired,
    url: string,
    preview: string,
    externalId: string,
  })).isRequired,
  progress: shape({
    completed: number.isRequired,
    total: number.isRequired,
    percentage: number.isRequired,
  }).isRequired,
  useCases: arrayOf(useCaseShape).isRequired,
  // Optional props for edit mode
  existingMedia: arrayOf(shape({
    id: string.isRequired,
    url: string,
    thumbUrl: string,
    mediumUrl: string,
    source: string,
  })),
  onRemoveExisting: func,
  enableDragAndDrop: bool,
}

export default FileUploadSection
